import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DialogService } from '../../components/dialogs/dialog.service';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable, of, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks, getBibleBooksCanon } from '../../../models/books.bible.model';
import { Group } from '../../../models/group.model';
import { QuestionType } from '../../../models/question.quiz.model';
import { UserModel } from '../../../models/user.model';
import { QuestionTypePipe } from '../../../pipes/questionType.pipe';
import { AiService } from '../../../services/ai.service';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { BibleReferencePickerComponent } from '../../components/inputs/bible-reference-picker/bible-reference-picker.component';

@Component({
  selector: 'app-create-questions',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnSubmitComponent,
    FormsModule,
    AsyncPipe,
    TitleCasePipe,
    ReactiveFormsModule,
    BibleReferencePickerComponent,
    BtnActionComponent,
    QuestionTypePipe,
  ],
  templateUrl: './create-questions.component.html',
  styleUrl: './create-questions.component.css',
})
export class CreateQuestionsComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  toastr: ToastrService = inject(ToastrService);
  store: Store<AppState> = inject(Store<AppState>);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  aiService: AiService = inject(AiService);
  quizService: QuizService = inject(QuizService);
  utilsService: UtilsService = inject(UtilsService);
  private logger = inject(NGXLogger);
  private actions$: Actions = inject(Actions);
  private dialogService: DialogService = inject(DialogService);
  private router: Router = inject(Router);
  private dialog: MatDialog = inject(MatDialog);
  fb = inject(FormBuilder);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;

  user: UserModel | null = null;
  group: Group | undefined = undefined;

  mainForm: FormGroup;
  aiForm: FormGroup;
  collapsedIndices = new Set<number>();
  myRoles$: Observable<any> = new Observable();
  aiSearching$: Observable<boolean> = of(false);
  loading$: Observable<boolean> = of(false);

  questionTypes = QuestionType;
  questionTypesList = Object.entries(QuestionType).map(([key, value]) => ({
    key,
    value,
  }));
  bibleBooks = ProtestantCanonBooks as Record<string, string>;

  exit$: Subject<void> = new Subject<void>();

  // Chat Panel properties
  chatOpen = false;
  chatMessages: { sender: 'user' | 'ai'; text: string }[] = [];
  chatInput = '';
  chatHistory: any[] = [];
  isStreaming = false;
  isSearchingDb = false;
  streamingText = '';
  totalChatTokens = { inputTokens: 0, outputTokens: 0, totalTokens: 0 };
  chatSession: any = null;

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.mainForm = this.fb.group({
      questions: this.fb.array([]),
    });

    this.aiForm = this.fb.group({
      reference: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group || undefined;
        this.bibleBooks = getBibleBooksCanon(group?.bibleType);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  isExpanded(index: number): boolean {
    return !this.collapsedIndices.has(index);
  }

  toggleCollapse(index: number) {
    if (this.collapsedIndices.has(index)) {
      this.collapsedIndices.delete(index);
    } else {
      this.collapsedIndices.add(index);
    }
  }

  get questions(): FormArray {
    return this.mainForm.get('questions') as FormArray;
  }

  addQuestion() {
    const questionGroup = this.fb.group({
      questionText: ['', [Validators.required, Validators.minLength(5)]],
      explanation: [''],
      difficulty: [1, [Validators.required, Validators.min(1)]],
      score: [10, [Validators.required, Validators.min(1)]],
      questionType: [this.questionTypesList[0].key, Validators.required],
      reference: [null],
      options: this.fb.array([]),
      correctOptionId: [null],
      simpleAnswer: [''],
      tags: [[]],
    });

    this.questions.push(questionGroup);

    // If default type is Multiple Choice, add two empty options by default
    this.addOption(this.questions.length - 1);
    this.addOption(this.questions.length - 1);

    setTimeout(() => {
      const element = document.getElementById(
        'question-' + (this.questions.length - 1),
      );
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  addOption(questionIndex: number) {
    const optionGroup = this.fb.group({
      id: [uuidv4()],
      text: ['', Validators.required],
    });
    this.getOptions(questionIndex).push(optionGroup);
  }

  getOptions(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('options') as FormArray;
  }

  removeOption(questionIndex: number, optionIndex: number) {
    this.logger.debug(
      `Removing option at index ${optionIndex} from question ${questionIndex}`,
    );
    this.getOptions(questionIndex).removeAt(optionIndex);
  }

  setCorrectOption(question: AbstractControl, optionId: string) {
    const qGroup = question as FormGroup;
    const currentId = qGroup.get('correctOptionId')?.value;

    const newId = currentId === optionId ? null : optionId;

    qGroup.get('correctOptionId')?.setValue(newId);
  }

  removeQuestion(index: number) {
    this.logger.debug(`Removing question at index ${index}`);
    this.questions.removeAt(index);
  }

  addTag(questionIndex: number, event: any) {
    const input = event.target as HTMLInputElement;
    let value = (input.value || '').trim().toLowerCase();
    if (value) {
      const question = this.questions.at(questionIndex) as FormGroup;
      const currentTags = question.get('tags')?.value || [];
      if (currentTags.length < 10 && !currentTags.includes(value)) {
        question.get('tags')?.setValue([...currentTags, value]);
      }
    }
    input.value = '';
  }

  removeTag(questionIndex: number, tagIndex: number) {
    const question = this.questions.at(questionIndex) as FormGroup;
    const currentTags = question.get('tags')?.value || [];
    const newTags = [...currentTags];
    newTags.splice(tagIndex, 1);
    question.get('tags')?.setValue(newTags);
  }

  onSubmit() {
    if (this.mainForm.invalid) {
      this.mainForm.markAllAsTouched();
      this.utilsService.onShowAlert(
        {
          code: 'VALIDATION_ERROR',
          title: 'Validation Error',
          message:
            'Please fix the validation errors on the form before submitting.',
        } as AppExceptionModel,
        false,
      );
      return;
    }
    this.chatOpen = false;
    this.loading$ = of(true);
    this.quizService
      .createQuestionsWithBilling(
        this.groupId!,
        this.mainForm.value.questions,
        this.totalChatTokens,
      )
      .then(() => {
        this.toastr.success('Questions saved successfully!');
        this.questions.clear();
        this.aiForm.reset();
        this.chatMessages = [];
        this.chatHistory = [];
        this.chatSession = null;
        this.totalChatTokens = {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        };
        this.loading$ = of(false);
      })
      .catch((error) => {
        this.utilsService.onShowAlert(
          {
            code: 'SAVE_QUESTIONS_ERROR',
            title: 'Save Failed',
            message:
              'An error occurred while saving questions. Please try again. Error details: ' +
              (error?.message || error),
          } as AppExceptionModel,
          false,
        );
        this.loading$ = of(false);
        this.logger.error('Error saving questions: ', error);
      });
  }

  scrollToBottom() {
    setTimeout(() => {
      const container = document.getElementById('chat-history-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }

  async toggleChat() {
    if (!this.chatOpen) {
      const isAvailable = await firstValueFrom(
        this.store.select(groupSelector.isAiCreditsAvailable)
      );
      if (!isAvailable) {
        const buyNow = await this.dialogService.confirm({
          title: 'Out of AI Credits',
          description:
            'Your group has run out of AI credits. Please purchase additional credits to use the AI quiz assistant.',
          action: 'Buy Now',
          cancelText: 'Cancel',
        });
        if (buyNow && this.groupId) {
          this.router.navigate(['/school', this.groupId, 'billing', 'addonsubscribe']);
        }
        return;
      }
    }

    this.chatOpen = !this.chatOpen;
    if (this.chatOpen) {
      if (this.chatMessages.length === 0) {
        this.chatMessages.push({
          sender: 'ai',
          text: 'Hi there! I am your AI Bible Quiz Assistant. Please provide a specific Bible reference or topic/theme (e.g. "Genesis 1" or "faith in Hebrews 11") to generate quiz questions.',
        });
      }
      this.scrollToBottom();
    }
  }

  async sendChatMessage() {
    if (!this.chatInput.trim() || this.isStreaming) return;

    const userMsgText = this.chatInput.trim();
    this.chatInput = '';

    this.chatMessages.push({ sender: 'user', text: userMsgText });
    this.scrollToBottom();

    // Prepare prompt including current form questions for edit state syncing
    const currentQuestions = this.questions.value;
    const prompt = `User Request: "${userMsgText}"

Current questions displayed on the screen (use these as the active list to modify, refine, or add to as requested by the user. If this list is empty, start fresh):
${JSON.stringify(currentQuestions, null, 2)}`;

    this.isStreaming = true;
    this.streamingText = '';

    if (!this.chatSession) {
      this.chatSession = this.aiService.startQuizChat(this.chatHistory);
    }

    try {
      let currentStreamResult =
        await this.chatSession.sendMessageStream(prompt);
      let hasFunctionCalls = true;

      while (hasFunctionCalls) {
        let fullTextResponse = '';
        for await (const chunk of currentStreamResult.stream) {
          fullTextResponse += chunk.text();
          this.streamingText =
            this.extractPartialChatResponse(fullTextResponse);
          this.scrollToBottom();
        }

        const finalResponse = await currentStreamResult.response;
        const usageMetadata = finalResponse.usageMetadata;
        if (usageMetadata) {
          this.totalChatTokens.inputTokens +=
            usageMetadata.promptTokenCount ?? 0;
          this.totalChatTokens.outputTokens +=
            usageMetadata.candidatesTokenCount ?? 0;
          this.totalChatTokens.totalTokens +=
            usageMetadata.totalTokenCount ?? 0;
          this.logger.debug('Running token usage:', this.totalChatTokens);
        }

        const calls = finalResponse.functionCalls();
        if (calls && calls.length > 0) {
          const parts: any[] = [];
          for (const call of calls) {
            if (call.name === 'searchExistingQuestions') {
              this.isSearchingDb = true;
              this.scrollToBottom();
              try {
                const args = call.args as any;
                const queryRef = {
                  book: args.book,
                  chapter: args.chapter ? Number(args.chapter) : undefined,
                  fromVerse: args.fromVerse
                    ? Number(args.fromVerse)
                    : undefined,
                  toVerse: args.toVerse ? Number(args.toVerse) : undefined,
                };
                const existing = await firstValueFrom(
                  this.quizService.getQuestions(this.groupId!, queryRef),
                );
                const simplified = existing.map((q) => ({
                  questionText: q.questionText,
                  questionType: q.questionType,
                  reference: q.reference,
                }));
                parts.push({
                  functionResponse: {
                    name: call.name,
                    response: { questions: simplified },
                  },
                });
              } catch (err) {
                this.logger.error(
                  'Error querying database for duplicates:',
                  err,
                );
                parts.push({
                  functionResponse: {
                    name: call.name,
                    response: { error: 'Database search failed' },
                  },
                });
              } finally {
                this.isSearchingDb = false;
                this.scrollToBottom();
              }
            }
          }
          if (parts.length > 0) {
            currentStreamResult =
              await this.chatSession.sendMessageStream(parts);
          } else {
            hasFunctionCalls = false;
          }
        } else {
          hasFunctionCalls = false;
          const rawJson = finalResponse.text();
          this.logger.debug('Final AI JSON Response:', rawJson);

          try {
            const parsed = JSON.parse(rawJson);
            const chatReply = parsed.chatResponse || 'Here are the questions.';

            if (
              parsed.questions &&
              Array.isArray(parsed.questions) &&
              parsed.questions.length > 0
            ) {
              this.mapResultToForm(parsed.questions);
            }

            if (parsed.submitQuestions === true) {
              if (this.mainForm.invalid) {
                this.mainForm.markAllAsTouched();
                const validationErrorMsg =
                  'I cannot submit the questions because there are validation errors on the form. Please resolve them and ask me to submit again.';
                this.chatMessages.push({
                  sender: 'ai',
                  text: chatReply
                    ? `${chatReply}\n\n${validationErrorMsg}`
                    : validationErrorMsg,
                });
              } else if (this.questions.length === 0) {
                const emptyErrorMsg =
                  'There are no questions in the form to submit. Please generate or add some questions first.';
                this.chatMessages.push({
                  sender: 'ai',
                  text: chatReply
                    ? `${chatReply}\n\n${emptyErrorMsg}`
                    : emptyErrorMsg,
                });
              } else {
                this.chatMessages.push({ sender: 'ai', text: chatReply });
                this.isStreaming = true;
                this.streamingText = 'Submitting questions to the database...';
                try {
                  await this.quizService.createQuestionsWithBilling(
                    this.groupId!,
                    this.mainForm.value.questions,
                    this.totalChatTokens,
                  );
                  this.toastr.success('Questions saved successfully!');
                  this.questions.clear();
                  this.aiForm.reset();
                  this.chatMessages.push({
                    sender: 'ai',
                    text: 'I have successfully created and saved the questions to the database!',
                  });
                  this.chatHistory = [];
                  this.chatSession = null;
                  this.totalChatTokens = {
                    inputTokens: 0,
                    outputTokens: 0,
                    totalTokens: 0,
                  };
                } catch (err: any) {
                  this.logger.error('Error submitting questions via AI:', err);
                  this.chatMessages.push({
                    sender: 'ai',
                    text:
                      'Failed to submit questions to the database: ' +
                      (err?.message || err),
                  });
                }
              }
            } else {
              this.chatMessages.push({ sender: 'ai', text: chatReply });
            }

            if (this.chatSession) {
              this.chatHistory = await this.chatSession.getHistory();
            }
          } catch (jsonErr) {
            this.logger.error(
              'Failed to parse AI JSON response:',
              jsonErr,
              rawJson,
            );
            this.chatMessages.push({
              sender: 'ai',
              text: 'Sorry, I encountered an issue parsing the generated questions. Let me know if you would like me to try again.',
            });
          }
        }
      }
    } catch (err: any) {
      this.logger.error('Error during AI chat streaming:', err);
      this.chatMessages.push({
        sender: 'ai',
        text: 'Sorry, there was a connection error. Please try again.',
      });
    } finally {
      this.isStreaming = false;
      this.isSearchingDb = false;
      this.streamingText = '';
      this.scrollToBottom();
    }
  }

  extractPartialChatResponse(partialJson: string): string {
    const match = partialJson.match(/"chatResponse"\s*:\s*"/);
    if (!match || match.index === undefined) {
      return '';
    }
    const startIndex = match.index + match[0].length;
    let result = '';
    let escaped = false;
    for (let i = startIndex; i < partialJson.length; i++) {
      const char = partialJson[i];
      if (escaped) {
        if (char === 'n') result += '\n';
        else if (char === 't') result += '\t';
        else result += char;
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        break; // End of string
      } else {
        result += char;
      }
    }
    return result;
  }

  mapResultToForm(results: any[]) {
    const questionsArray = this.mainForm.get('questions') as FormArray;
    questionsArray.clear();

    results.forEach((q) => {
      let qType: string = 'multipleChoice';
      const typeStr = q.questionType;
      if (typeStr === 'Multiple Choice' || typeStr === 'multipleChoice') {
        qType = 'multipleChoice';
      } else if (
        typeStr === 'Fill In The Blank' ||
        typeStr === 'fillInTheBlank'
      ) {
        qType = 'fillInTheBlank';
      } else if (typeStr === 'True/False' || typeStr === 'trueFalse') {
        qType = 'trueFalse';
      } else if (typeStr === 'Short Answer' || typeStr === 'shortAnswer') {
        qType = 'shortAnswer';
      }

      const questionGroup: FormGroup = this.fb.group({
        questionText: [q.questionText, Validators.required],
        difficulty: [
          q.difficulty || 1,
          [Validators.required, Validators.min(1)],
        ],
        score: [q.score || 10, [Validators.required, Validators.min(1)]],
        explanation: [q.explanation || ''],
        questionType: [qType, Validators.required],
        simpleAnswer: [q.simpleAnswer || ''],
        correctOptionId: [q.correctOptionId || ''],
        tags: [
          (q.tags || [])
            .map((t: string) => (t || '').trim().toLowerCase())
            .filter(Boolean),
        ],
        reference: [
          {
            book: q.reference?.book || '',
            chapter: q.reference?.chapter || undefined,
            verse: q.reference?.verse || undefined,
          },
        ],
      });

      if (qType === 'multipleChoice' || qType === 'trueFalse') {
        const optionsArray = this.fb.array(
          (q.options || []).map((opt: any) =>
            this.fb.group({
              id: [opt.id || uuidv4()],
              text: [opt.text, Validators.required],
            }),
          ),
        );
        questionGroup.addControl('options', optionsArray);
      } else {
        questionGroup.addControl('options', this.fb.array([]));
      }

      this.logger.debug('Question Group: ', questionGroup.value);
      questionsArray.push(questionGroup);
    });
  }
}
